import { Switch } from '../../../../components';
import {
    Card,
    Exercise,
    GhostButton,
    LabelBody,
    LabelBodyHeadline,
    LabelDisplay1,
    LabelDisplay2,
    ProgressBar,
    SecondaryButton,
    Slider,
    Spacer,
    SwirlyBox,
    Tip,
    Toggle,
} from '../../components';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { ExerciseStatus } from '../../interfaces';
import React from 'react';
import { normalizeInput } from '../../useSyncedState';
import { FeedbackText } from '../../components/Slider';
import {
    CheckCircle,
    ChevronDown,
    Module7Exercise1Illustration2,
} from '../../icons';
import {
    createQuestionFromConfiguration,
    QuestionView,
} from '../../../logbook/views/QuestionView';
import {
    cancelLocalWorryNotification,
    scheduleLocalWorryNotification,
} from '../../nativeAPI';
import { reportExerciseProgressAtom } from '../../data';
import { useSetAtom } from 'jotai/index';
import classNames from 'classnames';

const emotionalEnergyKey = 'emotionalEnergy';
const notificationTimeKey = 'notificationTime';
const notificationEnabledKey = 'notificationEnabled';
const numberOfPreviousPages = 5;

export const exerciseData312: ExerciseData = {
    ExerciseView: Module3Exercise1Part2,
    dataKeys: [emotionalEnergyKey, notificationTimeKey],
    questions: [
        {
            Answers: (props) => (
                <QuestionView
                    {...props}
                    title="Hvordan er din følelsesmæssige energi?"
                    description={
                        <>
                            I hvor høj grad overtænker du? <br />1 = i lav grad
                            og 4 = i meget høj grad
                        </>
                    }
                    answerTitle="Din score"
                />
            ),
            dataKeys: [emotionalEnergyKey],
        },
        createQuestionFromConfiguration({
            title: 'Bekymringsbuffer',
            description: 'Tidspunkt for min daglige bekymringsbuffer',
            dataKeys: [notificationTimeKey],
        }),
    ],
};

function Module3Exercise1Part2({ exercise, onContinue }: ExerciseViewProps) {
    const reportExerciseProgress = useSetAtom(reportExerciseProgressAtom);

    function saveNotificationSettings(
        notificationTime: string,
        notificationEnabled: boolean,
    ) {
        return reportExerciseProgress({
            exercise,
            updateRequest: {
                CurrentPage: 2,
                Data: {
                    [notificationTimeKey]: normalizeInput(notificationTime),
                    [notificationEnabledKey]: notificationEnabled.toString(),
                },
                UpdatedStatus: ExerciseStatus.InProgress,
            },
        });
    }

    return (
        <Exercise title="Følelsesmæssig energi">
            <ProgressBar
                current={exercise.CurrentPage + numberOfPreviousPages}
                total={11}
            />
            <Switch
                cases={{
                    1: (
                        <Page1
                            sliderValue={
                                exercise.Data[emotionalEnergyKey] != null
                                    ? Number(exercise.Data[emotionalEnergyKey])
                                    : null
                            }
                            onContinue={(sliderValue) => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                    data: {
                                        [emotionalEnergyKey]: normalizeInput(
                                            sliderValue.toString(),
                                        ),
                                    },
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            sliderValue={
                                exercise.Data[emotionalEnergyKey] != null
                                    ? Number(exercise.Data[emotionalEnergyKey])
                                    : null
                            }
                            notificationTime={
                                exercise.Data[notificationTimeKey] ?? ''
                            }
                            notificationEnabled={
                                (exercise.Data[
                                    notificationEnabledKey
                                ]?.toLowerCase() ?? 'true') === 'true'
                            }
                            onFinish={() => {
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: false,
                                });
                            }}
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: '3.1.3',
                                });
                            }}
                            saveNotificationSettings={saveNotificationSettings}
                        />
                    ),
                }}
                value={exercise.CurrentPage}
                default={1}
            />
        </Exercise>
    );
}

const feedbackTexts: { [key: number]: FeedbackText } = {
    1: {
        title: 'Din følelsesmæssige energi er i lav grad præget af overtænkning',
        text: 'Måske er du god til ikke at blive fanget i tankemylderet. Har du mon allerede nogle gode strategier, der hjælper dig med dette?',
    },
    2: {
        title: 'Din følelsesmæssige energi er i nogen grad præget af overtænkning',
        text: 'Du oplever det måske ind i mellem, men det fylder ikke det meste af tiden. At blive bevidst om, hvornår du overtænker, kan være et godt næste skridt.',
    },
    3: {
        title: 'Din følelsesmæssige energi er i høj grad præget af overtænkning',
        text: 'Det kan føles drænende at sidde fast i tankerne. Så at arbejde med at mindske overtænkning kunne være et godt sted at starte?',
    },
    4: {
        title: 'Din følelsesmæssige energi er i meget høj grad præget af overtænkning',
        text: 'Det kan komme til at tage meget af din energi. Det kan være en god idé at øve dig i at give din følelsesmæssige energi et boost.',
    },
};
type Page1Props = {
    sliderValue: number | null;
    onContinue: (sliderValue: number) => void;
};
function Page1({ sliderValue: _sliderValue, onContinue }: Page1Props) {
    const [sliderValue, setSliderValue] = React.useState<number | null>(
        _sliderValue,
    );
    return (
        <>
            <LabelDisplay2 className="my-10">
                Vurder din følelsesmæssige energi
            </LabelDisplay2>
            <LabelBody>
                Det er naturligt at opleve alle mulige følelser, både dem der
                føles svære og tunge og dem, der er lette og rare.
            </LabelBody>
            <LabelBody className="mt-10">
                Når vi er pressede, har vi en tendens til at falde i fælden med
                at overtænke. Det kan typisk forlænge den tid, vi befinder os i
                de svære følelser.
            </LabelBody>
            <LabelBody className="mt-10">
                Vi kan ikke konstant være fyldt med positive følelser, da det er
                en del af livet at opleve op- og nedture. Men vi kan arbejde på
                at begrænse, hvor meget overtænkningen får lov til at fylde i
                vores hverdag.
            </LabelBody>
            <Slider
                className="mb-30 mt-20"
                title="I hvor høj grad overtænker du?"
                description="1 = i lav grad og 4 = i meget høj grad"
                value={sliderValue}
                setValue={setSliderValue}
                feedbackTexts={sliderValue ? feedbackTexts[sliderValue] : null}
                backgroundImage={'/images/slider-background-2.svg'}
                min={0}
                hideZeroLabel={true}
            />
            <Spacer />
            <SecondaryButton
                disabled={!sliderValue}
                onClick={() => onContinue(sliderValue)}
            >
                Fortsæt til tippet
            </SecondaryButton>
        </>
    );
}

type Page2Props = {
    sliderValue: number | null;
    notificationTime: string;
    notificationEnabled: boolean;
    onFinish: () => void;
    onContinue: () => void;
    saveNotificationSettings: (
        notificationTime: string,
        notificationEnabled: boolean,
    ) => Promise<any>;
};
function Page2({
    sliderValue,
    notificationTime: _notificationTime,
    notificationEnabled: _notificationEnabled,
    onFinish,
    onContinue,
    saveNotificationSettings,
}: Page2Props) {
    const [notificationTime, setNotificationTime] =
        React.useState(_notificationTime);
    const [notificationEnabled, setNotificationEnabled] =
        React.useState(_notificationEnabled);
    const hasChanges =
        (notificationTime && notificationTime !== _notificationTime) ||
        notificationEnabled !== _notificationEnabled;
    const [changesSaved, setChangesSaved] = React.useState(false);

    const pendingTimeRef = React.useRef<string | null>(null);

    function handleNotificationSettingsChanged() {
        const [hourStr, minuteStr] = notificationTime.split(':');
        const hour = parseInt(hourStr, 10);
        const minute = parseInt(minuteStr, 10);

        if (notificationEnabled) {
            // remember what we just tried to schedule
            pendingTimeRef.current = notificationTime;
            scheduleLocalWorryNotification(hour, minute);
        } else {
            cancelLocalWorryNotification();
            saveNotificationSettings(notificationTime, false).then(() =>
                setChangesSaved(true),
            );
        }
    }

    const lastSavedTime = _notificationTime;

    (window as any).onNotificationScheduled = function (success: boolean) {
        if (success) {
            const finalTime = pendingTimeRef.current ?? _notificationTime;

            setNotificationEnabled(true);
            setNotificationTime(finalTime);

            saveNotificationSettings(finalTime, true).then(() =>
                setChangesSaved(true),
            );

            pendingTimeRef.current = null;
        } else {
            setNotificationTime(lastSavedTime);
            setNotificationEnabled(false);
            setChangesSaved(false);
        }
    };

    React.useEffect(() => {
        setChangesSaved(false);
    }, [notificationTime, notificationEnabled]);

    return (
        <>
            <LabelDisplay2>Bekymringsbuffer</LabelDisplay2>
            {sliderValue < 3 && (
                <>
                    <LabelBody className="my-10">
                        Det ser ud til, at overtænkning ikke fylder så meget for
                        dig. Hvad mon du gør, som fungerer godt?
                    </LabelBody>
                    <Module7Exercise1Illustration2 className="mx-auto my-30" />
                    <LabelBody>
                        Hvis du alligevel er nysgerrig på et brugbart redskab
                        til at håndtere overtænkningen, så læs mere om
                        bekymringsbufferen her eller gå videre til næste øvelse.
                    </LabelBody>
                    <GhostButton className="my-30" onClick={onContinue}>
                        Videre til mental energi
                    </GhostButton>
                    <LabelBody className="mb-30">
                        Bekymringsbufferen er et dagligt tidsrum, hvor du kan
                        opholde dig ved dét der er svært, og bekymre dig om alt
                        mellem himmel og jord. På den måde bliver det meget
                        lettere at give slip på dine bekymringer og
                        overtænkning, når det ikke er tid til at bekymre dig.
                    </LabelBody>
                </>
            )}
            {sliderValue > 2 && (
                <>
                    <LabelBody className="my-10">
                        Din score indikerer, at overtænkning fylder en del hos
                        dig, og det kan dræne din følelsesmæssige energi. Det er
                        helt almindeligt når man er presset.
                    </LabelBody>
                    <Module7Exercise1Illustration2 className="mx-auto my-30" />
                    <LabelBody>
                        Et tip der kan hjælpe, når tankerne løber løbsk, er at
                        indføre en bekymringsbuffer. Det er et dagligt tidsrum,
                        hvor du må dvæle ved alt det, der er svært og bekymre
                        dig om alt mellem himmel og jord.
                    </LabelBody>
                    <LabelBody className="mb-30 mt-10">
                        Pointen er, at når du ved, at der er sat tid af til dine
                        bekymringer senere, bliver det lettere at sige "ikke nu"
                        til tankerne, når de dukker op på andre tidspunkter.
                    </LabelBody>
                </>
            )}
            <Card>
                <LabelDisplay1 className="mb-10">
                    Indstil din bekymringsbuffer
                </LabelDisplay1>
                <LabelBody>
                    Eksperimenter med at fastsætte et dagligt kvarter, hvor du
                    må bekymre dig. Når kvarteret er slut, skal du forsøge at
                    lukke ned for bekymringstankerne, og først genbesøge dem i
                    næste bekymringsbuffer.
                </LabelBody>
                <LabelBodyHeadline className="mb-20 mt-30">
                    Min daglige bekymringsbuffer:
                </LabelBodyHeadline>
                <LabelBody className="mb-5">Starttidspunkt</LabelBody>
                <label className="relative mb-30 block">
                    <div
                        className={classNames(
                            'flex items-center justify-between border border-grey-textfield bg-white p-10 text-base',
                            notificationTime ? '' : 'italic',
                        )}
                    >
                        {notificationTime || 'Vælg tidspunkt'}
                        <ChevronDown className="text-grey" />
                    </div>
                    <input
                        type="time"
                        value={notificationTime}
                        onChange={(e) => setNotificationTime(e.target.value)}
                        className="absolute inset-0 h-full w-full opacity-0"
                    />
                </label>
                <LabelBody className="mb-15">Send notifikation</LabelBody>
                <div className="mb-30 flex flex-row items-center">
                    <Toggle
                        id="toggleId"
                        value={notificationEnabled}
                        onChangeValue={setNotificationEnabled}
                    />
                    <label
                        htmlFor="toggleId"
                        className="ml-10 cursor-pointer self-center text-body text-dark-red"
                    >
                        {notificationEnabled ? 'Til' : 'Fra'}
                    </label>
                </div>
                <GhostButton
                    disabled={!hasChanges}
                    onClick={handleNotificationSettingsChanged}
                >
                    Gem ændringer
                </GhostButton>
                {changesSaved && (
                    <div className="mt-30 flex flex-row">
                        <CheckCircle className="mr-10 self-center text-success" />
                        <LabelBodyHeadline>
                            Ændringerne er gemt
                        </LabelBodyHeadline>
                    </div>
                )}
            </Card>
            <LabelDisplay1 className="mt-20">
                Sådan får overtænkning mindre plads
            </LabelDisplay1>
            <LabelBody className="mt-10">
                Udenfor din bekymringsbuffer skal du træne dig selv i at give
                slip på dine bekymringer. Du kan eventuelt skrive dine
                bekymringer og tanker ned i logbogen, når de popper op i løbet
                af dagen, og så vende tilbage og give dem opmærksomhed, når det
                er buffer-tid.
            </LabelBody>
            <LabelBody className="mt-10">
                Her er øvelse nøgleordet, fordi det tager tid at vende dig selv
                af med at bekymre dig og overtænke.
            </LabelBody>
            <Tip className="my-30">
                Du kan med fordel lægge din buffer om eftermiddagen, og ikke om
                aftenen tæt på sengetid.
            </Tip>
            <SwirlyBox className="my-30">
                Vil du se på din mentale energi?
            </SwirlyBox>
            <Spacer />
            <SecondaryButton className="mb-20" onClick={onContinue}>
                Fortsæt
            </SecondaryButton>
            <GhostButton onClick={onFinish}>Vender tilbage senere</GhostButton>
        </>
    );
}
